import React, { useState } from 'react';
import { Select, Spin, message, Dropdown, Button } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import clienteAxios from '../../Utils/clienteAxios';
import setToken from '../../Utils/setToken';

/**
 * Cambio de empresa para quien tiene dos cuentas vinculadas.
 *
 * No se dibuja nada si la persona solo tiene una cuenta, que es el caso de casi
 * todos: el claim `accounts` viene vacío y este componente devuelve null.
 *
 * Al elegir otra empresa se pide un token nuevo, emitido para la cuenta de esa
 * empresa, y SE RECARGA LA PÁGINA ENTERA.
 *
 * La recarga NO es opcional y no debe "optimizarse" con navigate():
 * src/Middleware/RutaPrivada.jsx lee el token en el cuerpo del módulo (líneas
 * 6-8) y muta sus arreglos de rutas denegadas durante el import (25-27, 76-78).
 * Ese estado queda congelado desde que arranca la aplicación, así que sin
 * recarga la persona cambiaría de empresa conservando los permisos de la
 * anterior, sin ningún error visible. Aquí eso importa de verdad: hay quien es
 * manager en una empresa y equipo en la otra.
 */
const SelectorEmpresa = ({ accounts, collapsed }) => {
  const [cambiando, setCambiando] = useState(false);

  if (!accounts || accounts.length <= 1) {
    return null;
  }

  const activa = accounts.find((cuenta) => cuenta.is_active);

  const cambiarEmpresa = (userId) => {
    if (cambiando || userId === activa?.user_id) return;

    setCambiando(true);

    clienteAxios
      .post('/session/account', { user_id: userId })
      .then((respuesta) => {
        const token = respuesta.data.access_token;
        localStorage.setItem('token', token);
        setToken(token);
        // Recarga completa: ver el comentario de arriba antes de tocar esto.
        window.location.assign('/inicio');
      })
      .catch((error) => {
        setCambiando(false);
        message.error(
          error.response?.data?.message || 'No fue posible cambiar de empresa. Intenta de nuevo.'
        );
      });
  };

  // Plegado, el Sider mide 80px: el nombre de una empresa no cabe y se desborda.
  // Se cambia por un desplegable con icono, igual que el menú de sesión del
  // encabezado. Sigue siendo posible cambiar de empresa sin desplegar el menú.
  if (collapsed) {
    return (
      <div className="selector-empresa selector-empresa--plegado">
        <Dropdown
          trigger={['click']}
          disabled={cambiando}
          placement="bottomLeft"
          menu={{
            selectable: true,
            selectedKeys: [String(activa?.user_id)],
            items: accounts.map((cuenta) => ({
              key: String(cuenta.user_id),
              label: cuenta.company.name
            })),
            onClick: ({ key }) => cambiarEmpresa(Number(key))
          }}>
          <Button
            type="text"
            title={`Empresa: ${activa?.company?.name}`}
            aria-label="Cambiar de empresa"
            icon={cambiando ? <Spin size="small" /> : <SwapOutlined />}
            className="selector-empresa-boton"
          />
        </Dropdown>
      </div>
    );
  }

  return (
    <div className="selector-empresa">
      <span className="selector-empresa-etiqueta">EMPRESA</span>
      <Select
        value={activa?.user_id}
        onChange={cambiarEmpresa}
        disabled={cambiando}
        loading={cambiando}
        className="selector-empresa-control"
        suffixIcon={cambiando ? <Spin size="small" /> : <SwapOutlined />}
        options={accounts.map((cuenta) => ({
          value: cuenta.user_id,
          label: cuenta.company.name
        }))}
      />
    </div>
  );
};

SelectorEmpresa.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.number,
      company: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
      is_active: PropTypes.bool,
      is_primary: PropTypes.bool
    })
  ),
  collapsed: PropTypes.bool
};

export default SelectorEmpresa;
